import React from 'react';
import { Property } from '@/types/property';
import { ExternalLink, Bed, Bath, Car, Grid, MapPin, Home, Maximize, MinimizeIcon, Sparkles } from 'lucide-react';
import { parseDescription } from '@/utils/descriptionParser';

interface ExtendedProperty extends Property {
  price?: number;
  imageUrl?: string;
  descricao?: string;
  title?: string;
  location?: string;
  numero_suites?: number | string;
}

interface PropertyCardProps {
  property: ExtendedProperty;
  isMain?: boolean;
  className?: string;
}

// Helper function to safely format price
const formatPrice = (price: number | string | undefined): string => {
  // Handle undefined, null or NaN
  if (price === undefined || price === null || isNaN(Number(price))) {
    return 'Preço sob consulta';
  }
  
  // Convert string to number if needed
  const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d.-]/g, '')) : price;
  
  // Handle conversion failures
  if (isNaN(numericPrice)) {
    return 'Preço sob consulta';
  }
  
  // Format the price using toLocaleString with exact values
  return `R$ ${numericPrice.toLocaleString('pt-BR', { 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

const PropertyCard: React.FC<PropertyCardProps> = ({ 
  property, 
  isMain = false, 
  className = '' 
}) => {
  // Function to get numeric values safely
  const getNumericValue = (value: any): number => {
    if (value === undefined || value === null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };
  
  // Extract needed properties with proper type handling
  const price = property.price ?? property.valor_recente ?? 0;
  const formattedPrice = formatPrice(price);
  
  const imageUrl = property.imageUrl ?? property.img_url ?? '';
  
  const bedrooms = getNumericValue(property.numero_quartos);
  const suites = getNumericValue(property.numero_suites);
  const bathrooms = getNumericValue(property.numero_banheiros);
  const parkingSpaces = getNumericValue(property.numero_vagas);
  const areaUtil = getNumericValue(property.area_util);
  const areaTotal = getNumericValue(property.area_total);
  
  const title = property.title ?? 
                (property.tipo ? 
                 `${property.tipo} à ${property.finalidade || 'venda'}` : 
                 'Imóvel');
                 
  const location = property.location ?? 
                 (property.bairro ? 
                  `${property.bairro}${property.cidade ? `, ${property.cidade}` : ''}` : 
                  'Localização não informada');

  const propertyUrl = property.url;

  // Parse description features only for main property
  const descriptionFeatures = isMain ? parseDescription(property.descricao) : [];

  return (
    <div className={`bg-white rounded-lg shadow-sm overflow-hidden ${className} ${isMain ? 'border-2 border-blue-medium' : ''}`}>
      <div className="relative">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={title} 
            className="w-full h-48 md:h-56 object-cover"
          />
        ) : (
          <div className="w-full h-48 md:h-56 bg-gray-200 flex items-center justify-center">
            <Home size={48} className="text-gray-400" />
            <span className="sr-only">Imagem não disponível</span>
          </div>
        )}
        
        {isMain && (
          <div className="absolute top-3 left-3 bg-blue-dark text-white px-3 py-1 text-xs rounded-full">
            Imóvel Analisado
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex justify-between">
          <h3 className="text-xl font-semibold line-clamp-1">{title}</h3>
          {propertyUrl && (
            <a 
              href={propertyUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-dark"
              aria-label="Abrir anúncio original"
            >
              <ExternalLink size={18} />
            </a>
          )}
        </div>
        
        <div className="mt-2 text-blue-dark font-bold text-2xl">
          {formattedPrice}
        </div>
        
        <div className="mt-3 flex items-center text-gray-500">
          <MapPin size={16} className="flex-shrink-0" />
          <span className="ml-1 text-sm line-clamp-1">{location}</span>
        </div>
        
        {/* Características básicas em chips */}
        <div className="mt-4 flex flex-wrap gap-2">
          {/* Quartos e Suítes */}
          {(bedrooms > 0 || suites > 0) && (
            <div className="inline-flex items-center bg-blue-50 text-blue-dark rounded-full px-3 py-1">
              <Bed size={16} className="mr-1" />
              <span className="text-sm">
                {bedrooms} quarto{bedrooms !== 1 ? 's' : ''}
                {suites > 0 && ` (${suites} suíte${suites !== 1 ? 's' : ''})`}
              </span>
            </div>
          )}
          
          {/* Banheiros */}
          {bathrooms > 0 && (
            <div className="inline-flex items-center bg-blue-50 text-blue-dark rounded-full px-3 py-1">
              <Bath size={16} className="mr-1" />
              <span className="text-sm">{bathrooms} banheiro{bathrooms !== 1 ? 's' : ''}</span>
            </div>
          )}
          
          {/* Vagas */}
          {parkingSpaces > 0 && (
            <div className="inline-flex items-center bg-blue-50 text-blue-dark rounded-full px-3 py-1">
              <Car size={16} className="mr-1" />
              <span className="text-sm">{parkingSpaces} vaga{parkingSpaces !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Áreas em chips */}
        <div className="mt-2 flex flex-wrap gap-2">
          {/* Área Útil */}
          {areaUtil > 0 && (
            <div className="inline-flex items-center bg-gray-100 text-gray-700 rounded-full px-3 py-1">
              <MinimizeIcon size={16} className="mr-1" />
              <span className="text-sm">{areaUtil}m² útil</span>
            </div>
          )}
          
          {/* Área Total */}
          {areaTotal > 0 && areaTotal !== areaUtil && (
            <div className="inline-flex items-center bg-gray-100 text-gray-700 rounded-full px-3 py-1">
              <Maximize size={16} className="mr-1" />
              <span className="text-sm">{areaTotal}m² total</span>
            </div>
          )}
        </div>

        {/* Características da descrição em chips - apenas para o imóvel principal */}
        {isMain && descriptionFeatures.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {descriptionFeatures.map((feature, index) => (
              <div 
                key={`${feature.type}-${index}`}
                className="inline-flex items-center bg-purple-50 text-purple-700 rounded-full px-3 py-1"
              >
                <Sparkles size={16} className="mr-1" />
                <span className="text-sm">{feature.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
